import type { ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { existingChoiceLabel } from "@/lib/tina/festival-page";
import { CHOICE_OUTCOME_LABEL, dayRange } from "@/lib/tina/festivals";
import type {
  PageForm,
  PageFormAction,
} from "@/pages/tina/festivals/lib/page-form";
import type {
  ExistingRead,
  FestivalPageRead,
} from "@/types/tina-festival-page";

interface ExistingSectionProps {
  page: FestivalPageRead;
  form: PageForm;
  dispatch: (action: PageFormAction) => void;
  /** Each day's number by its form key, for "Use as Day 2". */
  dayNumbers: Map<string, number>;
  editable: boolean;
  /** The suggestions that ask about an event already listed here. */
  children?: ReactNode;
}

const REPLACE = "replace";
const NOT_PART = "not_part";
const USE_PREFIX = "use:";

/** The dates an event runs: the date parts of its start and end as the search read them, else its service day. */
function datesOf(line: ExistingRead): string {
  const first = line.start_iso?.slice(0, 10) ?? null;
  const last = line.end_iso?.slice(0, 10) ?? first;
  if (first !== null) return dayRange(first, last);
  return line.service_day ? dayRange(line.service_day, line.service_day) : "—";
}

/** The events already in Mood at this festival's venues on its days, and what becomes of each. */
export function ExistingSection({
  page,
  form,
  dispatch,
  dayNumbers,
  editable,
  children,
}: ExistingSectionProps) {
  // One option per date: "Use as Day 2" names the day by its date, as the body does.
  const dates = new Map<string, number>();
  for (const day of form.days) {
    dates.set(day.day, dayNumbers.get(day.key) ?? 0);
  }
  const dateList = [...dates.entries()].sort(([a], [b]) => (a < b ? -1 : 1));
  const first = dateList[0]?.[0] ?? null;

  const valueOf = (id: string): string => {
    const line = form.existing[id];
    if (!line) return REPLACE;
    if (line.choice === NOT_PART) return NOT_PART;
    if (
      line.choice === "use_as_day" &&
      line.day !== null &&
      dates.has(line.day)
    ) {
      return `${USE_PREFIX}${line.day}`;
    }
    return REPLACE;
  };

  const choose = (line: ExistingRead, value: string) => {
    if (value === NOT_PART) {
      dispatch({
        type: "set_choice",
        moodEventId: line.mood_event_id,
        choice: "not_part",
        day: null,
      });
    } else if (value.startsWith(USE_PREFIX)) {
      dispatch({
        type: "set_choice",
        moodEventId: line.mood_event_id,
        choice: "use_as_day",
        day: value.slice(USE_PREFIX.length),
      });
    } else {
      // Replace folds into the line's own day while that is a page day, else the first day.
      const own = [line.target_day, line.service_day].find(
        (day) => day !== null && dates.has(day),
      );
      dispatch({
        type: "set_choice",
        moodEventId: line.mood_event_id,
        choice: "replace",
        day: own ?? first,
      });
    }
  };

  return (
    <section className="flex flex-col gap-3 rounded-lg border p-4">
      <h2 className="text-lg font-semibold">Already in Mood</h2>
      <p className="text-sm text-muted-foreground">
        Events already in Mood for this festival. Say what becomes of each.
      </p>
      {page.existing.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No event in Mood at this festival&apos;s venues on its days.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Event</TableHead>
              <TableHead>Dates</TableHead>
              <TableHead>Venue</TableHead>
              <TableHead>Saves</TableHead>
              <TableHead>What to do</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {page.existing.map((line) => (
              <TableRow key={line.mood_event_id}>
                <TableCell>
                  <a
                    href={`/events/edit/${line.mood_event_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                  >
                    {line.name}
                  </a>
                  {line.suggested ? (
                    <Badge variant="outline" className="ml-2 font-normal">
                      Found while publishing
                    </Badge>
                  ) : null}
                </TableCell>
                <TableCell>{datesOf(line)}</TableCell>
                <TableCell>{line.venue_name ?? "—"}</TableCell>
                <TableCell>{line.saves === null ? "—" : line.saves}</TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <Select
                      value={valueOf(line.mood_event_id)}
                      disabled={!editable}
                      onValueChange={(value) => choose(line, value)}
                    >
                      <SelectTrigger
                        className="w-64"
                        aria-label={`What to do with ${line.name}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={REPLACE}>
                          {existingChoiceLabel("replace", null)}
                        </SelectItem>
                        {dateList.map(([date, number]) => (
                          <SelectItem key={date} value={`${USE_PREFIX}${date}`}>
                            {existingChoiceLabel("use_as_day", number)}
                          </SelectItem>
                        ))}
                        <SelectItem value={NOT_PART}>
                          {existingChoiceLabel("not_part", null)}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {line.outcome !== null ? (
                      <span className="text-xs text-muted-foreground">
                        {CHOICE_OUTCOME_LABEL[line.outcome]}
                      </span>
                    ) : null}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {children}
    </section>
  );
}
